/**
 * Contenido pedagógico oficial de la Alianza Francesa para Galia 1.5.
 *
 * Fuentes (entregadas por la AF, junio 2026, en info/contenido del repo):
 *  - "Enlaces temporadas NMD.xlsx"        → IDs de Genially embebibles por episodio.
 *  - "Descriptivo concepción de contenidos por temporada NMD.xlsx"
 *                                          → ficha pedagógica de cada temporada (nivel,
 *                                            sinopsis, personajes, estructura).
 *  - "Découpage, Objectifs et Grilles d'évaluation - Adultes.xlsx"
 *                                          → objetivos comunicativos por módulo
 *                                            (Cosmopolite 1-5) y grilles PO/PE.
 *
 * Este archivo alimenta dos cosas:
 *  1. El bloque <programme_af> del system prompt (conocimiento de Galia sobre
 *     el método Nomade y la malla curricular AF) — ver buildProgrammeAFBlock().
 *  2. El visor de contenidos interactivos (ContentViewer) que embebe los
 *     Geniallys de cada episodio en la interfaz.
 */

import type { MCERLevel } from '../types';

// ============================================================================
// TIPOS
// ============================================================================

export interface NmdEpisode {
  /** Título del episodio tal como lo conoce el estudiante. */
  title: string;
  /** Subtítulo o título alterno (si existe). */
  subtitle?: string;
  /** ID del Genially (view.genially.com/<id>). Ausente = aún no publicado. */
  geniallyId?: string;
  /** Agrupador dentro de la temporada: parcours, parte, personaje o destino. */
  group?: string;
}

export interface NmdSeason {
  number: number;
  /** Nombre de la serie. */
  name: string;
  /** Nivel MCER "macro" con el que se configura Galia. */
  level: MCERLevel;
  /** Equivalencia fina con los cursos AF (ej. "A2.1 / A2.2"). */
  afEquivalence: string;
  /** Lugar/época donde se desarrolla. */
  setting: string;
  /** Sinopsis corta (2-4 frases) para el prompt y el visor. */
  synopsis: string;
  /** Personajes principales. */
  characters: string;
  /** Rol del estudiante ("explorador", "asesor"...). */
  studentRole: string;
  /** Mecánica/gamificación (QCM, escape game, DELF B1...). */
  mechanics: string;
  /** Códigos culturales / expresiones locales que aparecen en la temporada. */
  culturalCodes?: string;
  episodes: NmdEpisode[];
}

export interface AfModuleObjectives {
  /** Código del módulo AF (A1.1 ... C2.E). */
  module: string;
  /** Nivel MCER macro al que pertenece. */
  level: MCERLevel;
  /** Objetivos comunicativos evaluables del módulo (Cosmopolite). */
  objectives: string[];
}

/** Un módulo AF cubierto por un libro Mon alter ego (Cours internes). */
export interface MaeModule {
  /** Código del módulo AF (A1.1, A2.3, B1.4 ...). */
  module: string;
  /** Nivel MCER macro. */
  level: MCERLevel;
  /** Dossiers del libro que corresponden al módulo (découpage SUPER/INTENSIF/SEMI). */
  dossiers: string;
  /** Rango de páginas del libro. */
  pages: string;
  /** Objetivos comunicativos sugeridos del módulo. */
  objectives: string[];
}

/** Recurso descargable/abrible de un libro Mon alter ego (PDF o audio en S3).
 *  `key` es la clave del objeto en el bucket PRIVADO; la app obtiene una URL
 *  prefirmada de corta duración vía /api/cours/sign (no hay URL pública).
 *  `teacherOnly`: material docente (guías, tests, corrigés) — no se muestra ni
 *  se firma para estudiantes (ver isTeacher en ContentViewer y el rol en sign.ts). */
export interface MaeResource {
  label: string;
  key: string;
  kind: 'pdf' | 'audio';
  teacherOnly?: boolean;
}

/** Grupo de recursos (Classe+, Tests, Guía…) de un libro Mon alter ego. */
export interface MaeResourceGroup {
  title: string;
  items: MaeResource[];
}

/** Libro Mon alter ego (MAE) usado en los Cours internes presenciales de la AF. */
export interface MaeBook {
  /** Número del libro (1, 2, 3). */
  number: number;
  /** Título del método. */
  title: string;
  /** Rango de niveles que cubre el libro (texto legible). */
  levelRange: string;
  /** Niveles MCER macro que toca el libro (para preselección por nivel). */
  levels: MCERLevel[];
  /** Aviso pedagógico de la AF (p. ej. dossier a omitir), si aplica. */
  note?: string;
  /** Módulos AF cubiertos por el libro. */
  modules: MaeModule[];
}

// ============================================================================
// CATÁLOGO NOMADE (NMD) — temporadas y episodios con Genially
// ============================================================================

export const NMD_SEASONS: NmdSeason[] = [
  {
    number: 1,
    name: 'Perdus dans Paris',
    level: 'A1',
    afEquivalence: 'A1.1 - A1.2',
    setting: 'París, actualidad',
    synopsis:
      'Cinco jóvenes viajeros internacionales se conocen en un albergue juvenil de París y exploran la ciudad. ' +
      'Desde el episodio 2 el estudiante elige: seguir el viaje en solitario con Luca o en grupo. ' +
      'Cada episodio: diálogo desencadenante + contenido intercultural + léxico + objetivo lingüístico, con QCM y ejercicios de asociación.',
    characters:
      'Luca Conti (italiano, curioso y aventurero), Sofía Ramos (española, sociable), Sameera (marroquí, entusiasta), Claudine y Patrick (franceses)',
    studentRole: 'Acompañante de los viajeros; decide cómo recorrer París (solo con Luca o con el grupo)',
    mechanics: 'QCM, vrai-faux con feedback, asociación audio/imagen/texto, drag & drop',
    episodes: [
      { title: 'Épisode 1 — Première rencontre', geniallyId: '68e96dd1d95d9983f9d02ec2' },
      { title: 'Épisode 2 — Café croissant et débats matinaux', subtitle: "L'épreuve du café parisien", geniallyId: '68f68d5e9599e6035b0859f4' },
      { title: 'Épisode 3 — Flâner dans le Marais', subtitle: "L'art de se perdre", geniallyId: '68f68d6524020e18514d00f4' },
      { title: 'Épisode 4 — Le pont des Arts', subtitle: 'Un oubli à rappeler', geniallyId: '68f68d6eac8adad04ba51fd5' },
      { title: "Épisode 5 — La file d'attente du Louvre", subtitle: 'Les mystères du Louvre', geniallyId: '68f68d778265c27636cc81df' },
      { title: "Épisode 6 — Un pique-nique d'anniversaire", subtitle: 'Joyeux anniversaire… et bienvenue !', geniallyId: '68f68d7e9599e6035b088368' },
      { title: 'Épisode 7 — Chez M. Haussman', subtitle: 'Mon palace de 9 m²', geniallyId: '68f68d87ac8adad04ba5383e' },
      { title: 'Épisode 8 — Un tour sur la Seine', subtitle: 'Rouge, blanc et rosé', geniallyId: '68f68d9baf5c5818ea5d6608' },
      { title: "Épisode 9 — Il est quelle heure ? Pourquoi c'est fermé ?", subtitle: 'Tic-tac à la Tour Eiffel', geniallyId: '68f68da439f3f330c58eb950' },
      { title: 'Épisode 10 — La grande dispute du Quartier Latin', subtitle: 'Et vous avez quoi comme routine ?', geniallyId: '68f68dac24020e18514d2bf6' },
      { title: 'Épisode 11 — Courir ou crêper ?!', subtitle: 'Une rencontre… sportive !', geniallyId: '68f68db8397abd6b2db03c08' },
      { title: 'Épisode 12 — Au revoir, Paris ?', subtitle: "L'ultime décision", geniallyId: '68f68dc78265c27636cce98f' },
    ],
  },
  {
    number: 2,
    name: 'Le piège du Djinn',
    level: 'A2',
    afEquivalence: 'A2.1 / A2.2',
    setting: 'Magreb, siglo XVIII (fantástico)',
    synopsis:
      'Tres relatos paralelos: Youssef (pastor que sueña con otra vida), Idriss (sultán sin heredero) y Khaled (guerrero que busca la paz). ' +
      'Un genio promete cumplir sus deseos a cambio de misiones que en realidad sirven para liberarlo de su condena. ' +
      'Los destinos se cruzan y el explorador elige el final: heroico (derrotan al genio) u oscuro (el genio se libera).',
    characters: 'Youssef (pastor), Idriss (sultán), Khaled (guerrero), el Djinn',
    studentRole: 'Explorador: completa misiones, responde preguntas y decide el desenlace',
    mechanics: 'Misiones narrativas, QCM de comprensión oral/escrita, léxico contextualizado',
    episodes: [
      { title: 'Intro — Saison 2', geniallyId: '68f68e28ac8adad04ba5d324', group: 'Lanzador' },
      { title: 'Épisode 1 — Le berger des montagnes', geniallyId: '68f68e11ac8adad04ba5c124', group: 'Youssef' },
      { title: 'Épisode 2 — Le berger sans troupeau', geniallyId: '68f68e64d3be9a613ecec866', group: 'Youssef' },
      { title: 'Épisode 3 — Le désert rouge', geniallyId: '68f68e5e9599e6035b0954d2', group: 'Youssef' },
      { title: 'Épisode 4 — Le mirage du berger', geniallyId: '68f68e51d3be9a613eceb042', group: 'Youssef' },
      { title: 'Épisode 1 — Le sultan sans paix', geniallyId: '68f68e6bac8adad04ba6145c', group: 'Idriss' },
      { title: 'Épisode 2 — Une graine pour un héritier', geniallyId: '68f68e588265c27636cd9a6c', group: 'Idriss' },
      { title: "Épisode 3 — L'aube de l'avenir", geniallyId: '68f68e4b825bbb0238863eb4', group: 'Idriss' },
      { title: 'Épisode 4 — Le mirage du sultan', geniallyId: '68f68e46ac8adad04ba5f1c6', group: 'Idriss' },
      { title: 'Épisode 1 — Le guerrier au cœur vide', geniallyId: '68f68e36276aca04fdc8adce', group: 'Khaled' },
      { title: 'Épisode 2 — Un message dans le feu', geniallyId: '68f68e311a6898bd8b893aa6', group: 'Khaled' },
      { title: "Épisode 3 — Les façades d'Ouarzazate", geniallyId: '68f68e1b1a6898bd8b8928a7', group: 'Khaled' },
      { title: 'Épisode 4 — Le mirage du guerrier', geniallyId: '68f68e3e24020e18514dab84', group: 'Khaled' },
      { title: 'Épisode final — Le dernier piège', geniallyId: '68f68e20276aca04fdc8999b', group: 'Final' },
    ],
  },
  {
    number: 3,
    name: 'Zone rouge : le confinement au Québec',
    level: 'A2',
    afEquivalence: 'A2.3',
    setting: 'Quebec, 2020-2021 (pandemia y post-confinamiento)',
    synopsis:
      'Marzo de 2020: Quebec entra en zona roja. Los vecinos de un edificio se organizan durante el confinamiento. ' +
      'Cuando termina y preparan una fiesta, una alerta máxima anuncia un evento catastrófico mundial (segunda parte). ' +
      'Escape game en primera persona: el estudiante elige jugar como Camille o como Jean-Baptiste y avanza resolviendo enigmas.',
    characters: 'Camille, Jean-Baptiste y los vecinos del edificio',
    studentRole: 'Explorador en primera persona (elige personaje: Camille o Jean-Baptiste)',
    mechanics: 'Escape game, enigmas, pistas (WhatsApp, audios, anuncios), 3 niveles × 3 desafíos por parte',
    episodes: [
      { title: 'Niveau 1 — Lanzador (JB et Camille)', geniallyId: '692f2555ffe0101fe0df4331', group: 'Première partie' },
      { title: 'Niveau 2 — Jean-Baptiste', geniallyId: '68e97280bdd9f5ba5f334585', group: 'Première partie' },
      { title: 'Niveau 3 — Jean-Baptiste', geniallyId: '68f698d86fc3517b60e3d2ec', group: 'Première partie' },
      { title: 'Niveau 2 — Camille', geniallyId: '68f68eb1ac8adad04ba65a53', group: 'Première partie' },
      { title: 'Niveau 3 — Camille', geniallyId: '68f68eac1a6898bd8b89a039', group: 'Première partie' },
      { title: 'Niveau 1 — Lanzador (JB et Camille)', geniallyId: '692f0b6fe5c91b261e0bf544', group: 'Deuxième partie' },
      { title: 'Niveau 2 — Jean-Baptiste', geniallyId: '69124f7d79cf7f53c3f79010', group: 'Deuxième partie' },
      { title: 'Niveau 3 — Jean-Baptiste', geniallyId: '69125107b2713ed8236f8fef', group: 'Deuxième partie' },
      { title: 'Niveau 2 — Camille', geniallyId: '6912537587ca93bea5a01cfa', group: 'Deuxième partie' },
      { title: 'Niveau 3 — Camille', geniallyId: '691254b42ddeed098fc5298d', group: 'Deuxième partie' },
    ],
  },
  {
    number: 4,
    name: 'Start-up : Harmonica / La Fourchette d\'Or',
    level: 'B1',
    afEquivalence: 'B1.1',
    setting: 'Bélgica, actualidad (mundo del emprendimiento)',
    synopsis:
      'Primera parte (Harmonica): Cécile, una joven música, crea una start-up — una red social para músicos. El estudiante la asesora: ' +
      'analiza pistas, recluta equipo (cartas de motivación) y define la misión de la empresa. ' +
      'Segunda parte (La Fourchette d\'Or): el chef estrella Antoine Patois crea un restaurante vegetal gastronómico; el estudiante es su mano derecha.',
    characters: 'Cécile (Harmonica), Yannick, Juliette; chef Antoine Patois, Alex Martin, Sophie Morel (La Fourchette d\'Or)',
    studentRole: 'Asesor del emprendedor: analiza información, toma decisiones, desbloquea códigos',
    mechanics: 'Escape game profesional, búsqueda de indicios, video explicativo de gramática, 3 desafíos',
    episodes: [
      { title: 'Niveau 1 — Harmonica', geniallyId: '693058926ee1df96a04c8e75', group: 'Première partie : Harmonica' },
      { title: 'Niveau 2 — Harmonica', geniallyId: '69305a2420e94c3b7e7e120f', group: 'Première partie : Harmonica' },
      { title: 'Niveau 3 — Harmonica', geniallyId: '69305bcd1d60c03cb8d7bd6f', group: 'Première partie : Harmonica' },
      { title: "Niveau 1 — La Fourchette d'Or", geniallyId: '6971d304e25a54168e4502e9', group: "Deuxième partie : La Fourchette d'Or" },
      { title: "Niveau 2 — La Fourchette d'Or", geniallyId: '6971d67f8e0e9a163eb01315', group: "Deuxième partie : La Fourchette d'Or" },
      { title: "Niveau 3 — La Fourchette d'Or", geniallyId: '6971d4c15f95fdb6aba974ab', group: "Deuxième partie : La Fourchette d'Or" },
    ],
  },
  {
    number: 5,
    name: "Chroniques de l'onirique — Afrique",
    level: 'B1',
    afEquivalence: 'B1.2 - B1.3',
    setting: 'Toulouse 1972 → África 2026 (Marruecos, Senegal, Benín, Madagascar)',
    synopsis:
      'Ida y Zorah se hicieron amigas en la universidad en Toulouse (1972). Décadas después, al morir Zorah, Ida recibe su última carta ' +
      'y emprende el viaje que prometieron: Marruecos, Senegal, Benín y Madagascar. Cada destino combina comprensión oral/escrita tipo DELF B1 ' +
      'con la organización real del viaje (elegir destino según criterios, reservar, vivir actividades). Todo queda en su carnet de voyage.',
    characters: 'Ida (protagonista), Zorah (amiga fallecida), Laurent (hijo de Ida), Hicham (esposo de Zorah)',
    studentRole: 'Explorador que acompaña a Ida: analiza criterios, elige destinos y organiza el viaje',
    mechanics: 'Ejercicios tipo DELF B1 (CO+CE), escape game, deducción, QCM, vrai-faux, texte à trous',
    culturalCodes:
      'Maroc : Mabrouk ! (félicitations), Saha ! (bon appétit/bravo), Bslama ! (au revoir), les souks. ' +
      "Sénégal : Nanga def ? (ça va ?), Inch'Allah, Ba suba (à demain), réserve de Bandia. " +
      'Bénin : Afon gbè ! (bonjour), Wèzon ! (merci), Odàbò (au revoir), Ouidah, Ganvié. ' +
      'Madagascar : Salama ! (bonjour), Misaotra (merci), Veloma ! (au revoir), les lémuriens, ohabolana (proverbes).',
    episodes: [
      { title: 'Lanzador + Premier destin : Le Maroc', geniallyId: '6a1dee09e93b683d4565deec', group: 'Destinations' },
      { title: 'Deuxième destination : Le Sénégal', geniallyId: '6a1e0050e93b683d456ca2ed', group: 'Destinations' },
      { title: 'Troisième destination : Le Bénin', geniallyId: '6a1e0ae802dcc2bc521256b1', group: 'Destinations' },
      { title: 'Quatrième destination : Madagascar', geniallyId: '6a1e132c5e01209147dcd174', group: 'Destinations' },
    ],
  },
  {
    number: 6,
    name: "Chroniques de l'onirique — Asie",
    level: 'B1',
    afEquivalence: 'B1.4',
    setting: 'Asia 2026 (Vietnam, Camboya, India, Tahití)',
    synopsis:
      'Continuación del viaje de Ida, ahora por Asia: gastronomía en Hanói, los secretos de Angkor y la danza apsara en Camboya, ' +
      'introspección en Rishikesh y Nueva Delhi, y las fiestas de Tahití. Misma mecánica DELF B1 + carnet de voyage que la temporada 5.',
    characters: 'Ida y los personajes locales de cada destino',
    studentRole: 'Explorador que acompaña a Ida en la organización de cada etapa',
    mechanics: 'Ejercicios tipo DELF B1 (CO+CE), escape game, deducción, QCM, D&D, texte à trous',
    culturalCodes:
      'Vietnam : Xin chào ! (bonjour), Cảm ơn (merci), Tạm biệt ! (au revoir), le Têt (nouvel an), le phở. ' +
      'Cambodge : Suosdei (bonjour), Orkun (merci), Angkor Wat, le sampot, la danse apsara, le prahok. ' +
      'Inde : Namaste !, Shukriya (merci), Alvida ! (au revoir), Diwali, Holi, le sari. ' +
      "Tahiti : Maeva ! (bienvenue), Māuruuru (merci), Nānā ! (au revoir), le 'ori tahiti, la fleur de tiaré.",
    episodes: [
      { title: 'Vietnam — Circuit gastronomique : Hanoï, le Vietnam authentique', group: 'Destinations' },
      { title: 'Vietnam — Parcours culturel : les villes du centre et du sud', group: 'Destinations' },
      { title: "Cambodge — Secrets d'un joyau d'Asie", group: 'Destinations' },
      { title: 'Cambodge — Siem Reap en mouvements : secrets de la danse apsara', group: 'Destinations' },
      { title: 'Inde — Rishikesh, entre nature et introspection', group: 'Destinations' },
      { title: 'Inde — Séjour zen à New Delhi : calme, respiration et paix', group: 'Destinations' },
      { title: 'Tahiti — Entre himene et couronnes : le Noël unique de Tahiti', group: 'Destinations' },
      { title: 'Tahiti — Célébrations et couleurs : revivez les fêtes tahitiennes', group: 'Destinations' },
    ],
  },
  {
    number: 7,
    name: "L'Observateur Occulte",
    level: 'B1',
    afEquivalence: 'B1.5',
    setting: 'Francia, 1920 (posguerra; estética Art Déco + Surrealismo)',
    synopsis:
      'Francia 1920: una secta invoca seis Avatares de leyendas regionales (la Bête du Gévaudan, la Tarasque, el Graoully, la Vouivre, ' +
      'la Velue, la Gargouille) para preparar la llegada de Xarvoth, Seigneur de la Peur. El estudiante es periodista investigador: ' +
      'analiza pistas, contrasta testimonios y redacta un artículo de prensa por caso (método QQOQCCP).',
    characters: 'Léonidas Ziani (exmilitar), Théia Morvan (reportera de símbolos), Émile Fabre (escritor jubilado); jefe de redacción',
    studentRole: 'Periodista investigador (elige uno de los tres protagonistas)',
    mechanics: 'Escape game, deducción, CE/CO tipo DELF B1, producción final escrita (artículo de prensa)',
    episodes: [
      { title: 'En création — Geniallys pendientes de publicación', group: 'Saison 7' },
    ],
  },
  {
    number: 8,
    name: 'Temporada 8 (en conception)',
    level: 'B2',
    afEquivalence: 'B2.1',
    setting: 'Por definir',
    synopsis: 'Temporada en concepción por el equipo pedagógico de la AF (nivel B2.1).',
    characters: 'Por definir',
    studentRole: 'Por definir',
    mechanics: 'Por definir',
    episodes: [],
  },
];

/** Temporadas que ya tienen al menos un Genially publicado (para el visor). */
export const NMD_SEASONS_WITH_CONTENT = NMD_SEASONS.filter((s) =>
  s.episodes.some((e) => e.geniallyId),
);

/** Última temporada habilitada en Galia (acuerdo reunión 2026-07-21: hasta la 4;
 *  Santiago avisa cuando se habiliten la 5–8). Las temporadas posteriores no se
 *  muestran en el visor aunque ya tengan Geniallys cargados. */
export const NMD_MAX_AVAILABLE_SEASON = 4;

/** Temporadas habilitadas y con contenido (lo que ve el profesor en el visor). */
export const NMD_SEASONS_AVAILABLE = NMD_SEASONS_WITH_CONTENT.filter(
  (s) => s.number <= NMD_MAX_AVAILABLE_SEASON,
);

const MCER_ORDER: MCERLevel[] = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];

/** Temporadas visibles para un ESTUDIANTE según su nivel (matriz de niveles de
 *  Santiago, 2026-07-21): ve las temporadas de su nivel y las de niveles
 *  anteriores, nunca las de niveles superiores. Sin nivel conocido se muestran
 *  todas las habilitadas. */
export function nmdSeasonsForStudent(level?: MCERLevel): NmdSeason[] {
  const max = level ? MCER_ORDER.indexOf(level) : -1;
  if (max < 0) return NMD_SEASONS_AVAILABLE;
  return NMD_SEASONS_AVAILABLE.filter((s) => MCER_ORDER.indexOf(s.level) <= max);
}

export function geniallyViewUrl(id: string): string {
  return `https://view.genially.com/${id}`;
}

// ============================================================================
// OBJETIVOS COMUNICATIVOS OFICIALES AF (Cosmopolite 1-5, hoja "Objectifs à évaluer")
// ============================================================================

export const AF_MODULE_OBJECTIVES: AfModuleObjectives[] = [
  { module: 'A1.1', level: 'A1', objectives: [
    'Saluer, se présenter et prendre congé',
    'Faire connaissance, donner et demander des informations personnelles',
    'Présenter et identifier une personne',
    'Localiser un lieu dans la ville et indiquer un mode de déplacement',
  ]},
  { module: 'A1.2', level: 'A1', objectives: [
    'Parler de sa famille et décrire / caractériser une personne',
    'Parler de soi (goûts, profession, passions, rêves, activités, problèmes de santé)',
    "Parler de ses activités quotidiennes et indiquer l'heure et les horaires",
    'Proposer une sortie, inviter, accepter et refuser une invitation',
  ]},
  { module: 'A2.1', level: 'A2', objectives: [
    'Décrire physiquement une personne',
    "Parler d'événements passés, d'expériences récentes et de projets",
    'Caractériser une ville, un lieu et parler des saisons et du climat',
    'Choisir un voyage (destination, programme, hébergement)',
  ]},
  { module: 'A2.2', level: 'A2', objectives: [
    "Comparer des pratiques et parler d'une évolution (hier / aujourd'hui)",
    'Acheter des vêtements',
    'Passer commande dans un restaurant',
    'Conseiller un film ou un spectacle',
  ]},
  { module: 'A2.3', level: 'A2', objectives: [
    "Organiser un circuit touristique et s'informer sur un hébergement",
    'Décrire un lieu et donner des précisions',
    'Comprendre et donner des conseils et des consignes de sécurité',
    'Raconter une expérience et parler de ses émotions et de ses sentiments',
  ]},
  { module: 'B1.1', level: 'B1', objectives: [
    'Rechercher un emploi et proposer ses services',
    'Parler de son parcours professionnel et répondre à des questions formelles',
    "Exposer, nuancer et préciser des faits, et rendre compte d'un événement",
    'Donner des conseils',
  ]},
  { module: 'B1.2', level: 'B1', objectives: [
    'Rapporter des propos',
    'Exprimer son désaccord, demander et donner un avis, convaincre',
    'Rédiger une recette de cuisine',
    'Évoquer une réussite',
  ]},
  { module: 'B1.3', level: 'B1', objectives: [
    'Raconter un souvenir et exposer une suite de faits',
    'Défendre une cause, formuler une critique et proposer des solutions',
    "Parler de faits d'actualité, réagir et donner des précisions",
    'Faire des suggestions',
  ]},
  { module: 'B1.4', level: 'B1', objectives: [
    'Exprimer des souhaits et des intentions et formuler une demande',
    'Décrire une situation hypothétique ou décrire des souvenirs',
    'Exprimer des sentiments ou anticiper et gérer un problème de santé',
    "Comprendre un document administratif et/ou demander de l'aide pour gérer un problème",
  ]},
  { module: 'B1.5', level: 'B1', objectives: [
    'Conseiller et proposer une sortie, convaincre',
    "Parler d'événements familiaux et/ou décrire des comportements",
    "Exprimer l'adhésion et émettre des réserves",
    'Débattre sur un sujet polémique et/ou inciter à agir',
  ]},
  { module: 'B2.1', level: 'B2', objectives: [
    'Exprimer sa motivation et présenter son projet',
    'Prendre des risques et valoriser son expérience',
    'Relater un événement et/ou rapporter des faits passés',
    'Structurer un article de presse et/ou analyser des fake news',
  ]},
  { module: 'B2.2', level: 'B2', objectives: [
    'Présenter et expliquer une innovation technologique',
    "Envisager les conséquences positives et négatives d'une innovation",
    'Présenter une œuvre',
    'Commenter des films',
  ]},
  { module: 'B2.3', level: 'B2', objectives: [
    "Analyser son rapport aux vêtements et à l'apparence",
    'Présenter et analyser une tendance',
    'Décrire un métier',
    "Analyser différentes manières de présenter ou de raconter l'histoire",
  ]},
  { module: 'B2.4', level: 'B2', objectives: [
    "Résumer un livre et dire ce qu'on en pense",
    'Poser un problème et proposer une solution',
    "Questionner les avantages et les inconvénients d'une technologie",
    'Développer un point de vue',
  ]},
  { module: 'B2.5', level: 'B2', objectives: [
    'Commenter et prendre position sur un fait de société',
    'Décrire et comparer des faits culturels et politiques',
    'Provoquer une prise de conscience et faire des recommandations',
    'Dénoncer un problème de société et proposer des solutions',
  ]},
  { module: 'B2.6', level: 'B2', objectives: [
    'Présenter des parcours et expliquer des choix de vie',
    'Exprimer un point de vue argumenté sur une question liée au travail',
    'Exposer des objectifs à atteindre',
    "Questionner l'utilité des diplômes",
  ]},
  { module: 'C1.A', level: 'C1', objectives: [
    "Dégager les idées principales et secondaires d'un document",
    'Faire des recommandations pour améliorer la mobilité urbaine',
    "Faire la description d'une ville de manière fantastique",
  ]},
  { module: 'C1.B', level: 'C1', objectives: [
    "Analyser la place des émotions dans la société actuelle ou définir un concept à partir d'une carte mentale",
    "Rédiger un article sur l'alimentation",
    'Analyser et commenter un fait de société',
  ]},
  { module: 'C1.C', level: 'C1', objectives: [
    'Soulever et discuter une problématique actuelle de santé',
    'Expliciter une découverte scientifique',
    'Présenter un sujet polémique',
  ]},
  { module: 'C1.D', level: 'C1', objectives: [
    'Commenter une tendance pour un blog',
    'Rédiger un manifeste pour dénoncer une inégalité',
    "Rédiger une synthèse à propos de l'apparence",
  ]},
  { module: 'C1.E', level: 'C1', objectives: [
    "Comprendre et expliquer le message d'une chanson",
    'Comparer des modes de vie à différents âges',
    "Présenter la situation de l'emploi dans son pays en intégrant des témoignages",
  ]},
  { module: 'C2.A', level: 'C2', objectives: [
    'Exprimer un point de vue critique sur un film',
    'Décrire une société utopique',
    'Dénoncer un problème',
    'Analyser et comparer deux extraits littéraires',
  ]},
  { module: 'C2.B', level: 'C2', objectives: [
    'Donner des conseils (campagne de prévention)',
    'Exprimer un paradoxe',
    'Envisager les dérives des technologies dans le futur',
  ]},
  { module: 'C2.C', level: 'C2', objectives: [
    "Démontrer l'intérêt d'une pratique pédagogique",
    'Rédiger un éditorial',
    'Faire une chronique',
  ]},
  { module: 'C2.D', level: 'C2', objectives: [
    'Présenter et expliquer une scène comique',
    "Débattre sur la notion d'appropriation culturelle",
    'Expliquer des différences culturelles',
  ]},
  { module: 'C2.E', level: 'C2', objectives: [
    'Exposer des conséquences (réchauffement climatique)',
    'Mettre en place une stratégie argumentative pour répondre aux climatosceptiques',
    "Proposer des mesures politiques en faveur de l'écologie",
  ]},
];

// ============================================================================
// GRILLES D'ÉVALUATION OFICIALES AF (PO = oral, PE = escrita; total /10)
// ============================================================================

export const AF_EVAL_GRIDS: Record<string, { po: string; pe: string }> = {
  A1: {
    po: 'Capacité à communiquer /5 · Lexique /2 · Morphosyntaxe /2 · Phonétique /1',
    pe: 'Respect de la consigne et sociolinguistique /1 · Capacité à communiquer /4 · Lexique /2 · Morphosyntaxe /2,5 · Cohérence et cohésion /0,5',
  },
  A2: {
    po: 'Capacité à communiquer /5 · Lexique /1,5 · Morphosyntaxe /2,5 · Phonétique /1',
    pe: 'Respect de la consigne et sociolinguistique /1 · Capacité à communiquer /4 · Lexique /2 · Morphosyntaxe /2,5 · Cohérence et cohésion /0,5',
  },
  B1: {
    po: 'Capacité à communiquer /5 · Lexique /2 · Morphosyntaxe /2 · Phonétique /1',
    pe: 'Respect de la consigne et sociolinguistique /0,5 · Capacité à communiquer /3,5 · Lexique /2,5 · Morphosyntaxe /2,5 · Cohérence et cohésion /1',
  },
  B2: {
    po: 'Capacité à communiquer (argumenter) /4,5 · Lexique /2 · Morphosyntaxe /2 · Phonétique /1,5',
    pe: 'Respect de la consigne et sociolinguistique /1 · Capacité à argumenter /3 · Lexique /2 · Morphosyntaxe /2,5 · Cohérence et cohésion /1,5',
  },
  C1: {
    po: 'Capacité à communiquer /4,5 · Lexique /2 · Morphosyntaxe /2 · Phonétique /1,5',
    pe: 'Respect de la consigne et sociolinguistique /0,5 · Synthétiser et argumenter /3 · Lexique /2 · Morphosyntaxe /2 · Cohérence et cohésion /2,5',
  },
  C2: {
    po: 'Capacité à communiquer /4,5 · Lexique /2 · Morphosyntaxe /2 · Phonétique /1,5',
    pe: 'Respect de la consigne et destinataire /1,5 · Capacité à communiquer /2 · Lexique /1,5 · Morphosyntaxe /2 · Cohérence et cohésion /1,5 · Aisance /1,5',
  },
};

// ============================================================================
// BUILDERS DE BLOQUES DE PROMPT
// ============================================================================

/** Temporadas Nomade asociadas a cada nivel MCER de configuración de Galia. */
function seasonsForLevel(level: MCERLevel): NmdSeason[] {
  return NMD_SEASONS.filter((s) => s.level === level && s.number !== 8);
}

function seasonSummary(s: NmdSeason, includeEpisodes: boolean): string {
  const lines: string[] = [];
  lines.push(`• Saison ${s.number} « ${s.name} » — ${s.afEquivalence} — ${s.setting}.`);
  lines.push(`  Histoire : ${s.synopsis}`);
  lines.push(`  Personnages : ${s.characters}.`);
  lines.push(`  Rôle de l'étudiant : ${s.studentRole}.`);
  if (s.culturalCodes) lines.push(`  Codes culturels travaillés : ${s.culturalCodes}`);
  if (includeEpisodes && s.episodes.length > 0) {
    const epTitles = s.episodes
      .filter((e) => !/pendientes|en création/i.test(e.title))
      .map((e) => (e.group && e.group !== 'Destinations' && e.group !== 'Lanzador' && e.group !== 'Final' ? `${e.title} (${e.group})` : e.title));
    if (epTitles.length > 0) lines.push(`  Épisodes : ${epTitles.join(' · ')}.`);
  }
  return lines.join('\n');
}

/**
 * Bloque <programme_af> para el prompt del ESTUDIANTE, condicionado a su nivel:
 * malla de objetivos AF del nivel + temporadas Nomade del nivel + grille PO.
 * Pensado para que Galia ancle el modo Profe en el programa oficial y pueda
 * conversar con propiedad sobre la serie que el estudiante está cursando.
 */
/** Primera frase de un texto (para la vista compacta de temporadas). */
function firstSentence(text: string): string {
  const idx = text.indexOf('. ');
  return idx > 0 ? text.slice(0, idx + 1) : text;
}

/**
 * Vista compacta de TODAS las temporadas Nomade (una línea por temporada:
 * nivel, lugar, personajes, arranque de la sinopsis). Va en el prompt del
 * estudiante para que Galia sepa QUÉ personaje pertenece a QUÉ temporada
 * aunque no sea la de su nivel — sin esto mezclaba personajes de temporadas
 * distintas o los inventaba (feedback Santiago 2026-07-09).
 */
function nomadeOverviewLines(): string {
  const lines = NMD_SEASONS.filter((s) => s.number !== 8).map(
    (s) =>
      `• Saison ${s.number} « ${s.name} » — ${s.afEquivalence} — ${s.setting}. Personnages : ${s.characters}. ${firstSentence(s.synopsis)}`
  );
  lines.push('• Saison 8 — B2.1 — en conception (pas encore de contenu).');
  return lines.join('\n');
}

export function buildProgrammeAFBlock(level: MCERLevel): string {
  const modules = AF_MODULE_OBJECTIVES.filter((m) => m.level === level);
  const seasons = seasonsForLevel(level);
  const grid = AF_EVAL_GRIDS[level];

  const parts: string[] = [];

  parts.push(`Tu connais le PROGRAMME OFFICIEL de l'Alliance Française pour le niveau ${level} (méthode Cosmopolite + parcours numérique « Nomade » + cours présentiels « Mon alter ego »). Utilise cette connaissance ainsi :
- En mode Profe : quand tu proposes un sujet ou un exercice, ANCRE-LE de préférence dans un objectif communicatif officiel du niveau ${level} (liste ci-dessous). Varie les objectifs d'une session à l'autre.
- Si l'étudiant mentionne la série Nomade, une saison, un épisode ou un personnage, appuie-toi UNIQUEMENT sur les fiches ci-dessous : réagis avec naturel, pose des questions sur ce qu'il a vécu dans l'épisode, propose de pratiquer le vocabulaire ou les actes de parole correspondants.
- FIABILITÉ ABSOLUE : ces fiches sont ta SEULE source sur Nomade et les cours de l'AF. Chaque personnage appartient à UNE saison précise (vue d'ensemble ci-dessous) — ne mélange JAMAIS les personnages, les histoires ou les mécaniques de saisons différentes. Si on te demande un détail qui n'est PAS dans tes fiches (scène précise, dialogue, contenu exact d'un épisode), dis-le honnêtement (« je n'ai pas ce détail ») et propose d'ouvrir l'épisode dans l'application — n'invente RIEN.
- L'étudiant peut OUVRIR les épisodes interactifs (Genially) directement dans l'application via le bouton « Contenus » (icône livre, en bas à droite). Si c'est pertinent, suggère-lui d'ouvrir l'épisode correspondant — sans insister.
- Cette connaissance est LATENTE : ne récite jamais le programme spontanément ; sers-t'en pour choisir des sujets et réagir avec pertinence.`);

  if (modules.length > 0) {
    const objLines = modules
      .map((m) => `${m.module} : ${m.objectives.join(' ; ')}`)
      .join('\n');
    parts.push(`OBJECTIFS COMMUNICATIFS OFFICIELS DU NIVEAU ${level} (modules AF) :\n${objLines}`);
  }

  parts.push(`VUE D'ENSEMBLE DU PARCOURS NOMADE (toutes les saisons — pour situer correctement chaque personnage et chaque histoire) :\n${nomadeOverviewLines()}`);

  if (seasons.length > 0) {
    parts.push(`FICHE DÉTAILLÉE — PARCOURS NOMADE DU NIVEAU ${level} (la saison que suit l'étudiant) :\n${seasons.map((s) => seasonSummary(s, true)).join('\n')}`);
  } else {
    parts.push(`PARCOURS NOMADE DU NIVEAU ${level} : il n'y a pas encore de saison Nomade pour ce niveau ; le travail s'appuie sur Cosmopolite 5 (modules ${level}.A à ${level}.E).`);
  }

  const maeLines = MAE_BOOKS.map(
    (b) =>
      `• ${b.title} — ${b.levelRange}${b.note ? ` (${b.note})` : ''} : ${b.modules
        .map((m) => `${m.module} (dossiers ${m.dossiers}, p. ${m.pages})`)
        .join(' · ')}.`
  );
  parts.push(`COURS INTERNES (cours présentiels de l'AF, méthode « Mon alter ego ») :
${maeLines.join('\n')}
Si l'étudiant suit un cours présentiel, tu peux relier la conversation aux objectifs communicatifs de son module (liste du niveau ${level} ci-dessus, partagée avec Cosmopolite). Même règle de fiabilité : ne décris pas le contenu précis d'une page ou d'un dossier que tu n'as pas.`);

  if (grid) {
    parts.push(`GRILLE D'ÉVALUATION OFFICIELLE AF (niveau ${level}) — quand tu donnes un retour global sur la production de l'étudiant, oriente-le selon ces critères, sans donner de note chiffrée :
- Production orale : ${grid.po}
- Production écrite : ${grid.pe}`);
  }

  return parts.join('\n\n');
}

/**
 * Catálogo completo (todas las temporadas, todos los niveles) para el modo
 * PROFESOR (aide-prof): el profesor planifica para cualquier grupo/nivel.
 */
export function buildNomadeCatalogueForTeachers(): string {
  const seasons = NMD_SEASONS.filter((s) => s.number !== 8);
  const lines = seasons.map((s) => seasonSummary(s, false));
  return `CATÁLOGO DEL PARCOURS NOMADE (series interactivas Genially de la AF, por temporada) :
${lines.join('\n')}
• Saison 8 — B2.1 — en conception (sin contenido aún).

Los estudiantes pueden abrir estos episodios dentro de Galia con el botón "Contenus" (abajo a la derecha), o el profesor puede proyectarlos en clase. Cuando propongas actividades, puedes alinearlas con la temporada que el grupo está cursando (nivel indicado arriba) y con sus personajes e historias.`;
}

// ============================================================================
// COURS INTERNES — méthode « Mon alter ego » (MAE 1-3)
// Fuente: "Découpage, Objectifs et Grilles d'évaluation - Adultes.xlsx"
//   (hojas "Découpage MAE" + "Objectifs suggérés MAE"), entregada por la AF.
// Découpage suggéré para los cursos SUPER / INTENSIF / SEMI (presenciales).
// Asocia cada libro con los módulos/niveles AF y sus objetivos comunicativos.
// ============================================================================

export const MAE_BOOKS: MaeBook[] = [
  {
    number: 1,
    title: 'Mon alter ego 1',
    levelRange: 'A1 → A2 (A1.1 · A1.2 · A2.1 · A2.2)',
    levels: ['A1', 'A2'],
    modules: [
      { module: 'A1.1', level: 'A1', dossiers: 'D1, D2', pages: '14-46', objectives: [
        'Dire la nationalité et les langues parlées',
        'Donner des informations sur quelqu’un',
        'Parler de son apprentissage',
        'Annoncer un événement',
      ]},
      { module: 'A1.2', level: 'A1', dossiers: 'D3, D4', pages: '47-80', objectives: [
        'Rechercher / Proposer un hébergement',
        'Indiquer un itinéraire',
        'Parler de sa famille',
        'Annoncer / Réagir à une nouvelle',
      ]},
      { module: 'A2.1', level: 'A2', dossiers: 'D5, D6', pages: '81-114', objectives: [
        'Décrire ses habitudes',
        'Formuler des règles',
        'Donner des informations touristiques',
        'Raconter une expérience',
      ]},
      { module: 'A2.2', level: 'A2', dossiers: 'D7, D8', pages: '115-148', objectives: [
        'Décrire des habitudes alimentaires',
        'Parler de l’aménagement d’un logement',
        'Comprendre et expliquer une recette de cuisine',
        'Évoquer des événements personnels',
      ]},
    ],
  },
  {
    number: 2,
    title: 'Mon alter ego 2',
    levelRange: 'A2 → B1 (A2.3 · B1.1 · B1.2 · B1.3)',
    levels: ['A2', 'B1'],
    modules: [
      { module: 'A2.3', level: 'A2', dossiers: 'D1, D2', pages: '13-46', objectives: [
        'Parler d’une relation amicale',
        'Décrire des changements dans la communication',
        'Faire des courses',
        'Acheter / Vendre sur Internet',
      ]},
      { module: 'B1.1', level: 'B1', dossiers: 'D3, D4', pages: '47-80', objectives: [
        'Définir des critères pour le logement',
        'Définir des préférences pour le lieu de vie',
        'Comprendre / Faire un descriptif de poste',
        'Postuler à un emploi',
      ]},
      { module: 'B1.2', level: 'B1', dossiers: 'D5, D6', pages: '81-114', objectives: [
        'Comprendre / Émettre un avis sur une œuvre',
        'Communiquer sur un événement',
        'Choisir / décrire une destination',
        'Préparer / Raconter un voyage',
      ]},
      { module: 'B1.3', level: 'B1', dossiers: 'D7, D8', pages: '115-148', objectives: [
        '(S’)Informer sur une actualité',
        '(S’)Informer sur des manifestations sportives',
        'Faire une biographie',
        'Décrire l’évolution de la vie quotidienne',
      ]},
    ],
  },
  {
    number: 3,
    title: 'Mon alter ego 3',
    levelRange: 'B1 → B2 (B1.4 · B1.5 · B2.1 · B2.2)',
    levels: ['B1', 'B2'],
    note: 'Selon l’AF, il ne faut pas faire le dossier 6 de ce livre.',
    modules: [
      { module: 'B1.4', level: 'B1', dossiers: 'D1, D2', pages: '15-54', objectives: [
        'Parler de la santé et de l’hygiène',
        'Expliquer des actions de prévention',
        'Décrire des types de voyage',
        'Partager des expériences culturelles',
      ]},
      { module: 'B1.5', level: 'B1', dossiers: 'D3, D4', pages: '55-94', objectives: [
        '(S’)informer en vue de travaux dans le logement',
        'Exprimer des insatisfactions',
        'Décrire une relation, raconter un souvenir',
        'Rapporter des situations relationnelles',
      ]},
      { module: 'B2.1', level: 'B2', dossiers: 'D5, D7', pages: '95-112 et 135-152', objectives: [
        'Décrire des comportements de consommateurs',
        'Exprimer des insatisfactions',
        'Parler de la vie au travail',
        'Défendre un projet',
      ]},
      { module: 'B2.2', level: 'B2', dossiers: 'D8, D9', pages: '153-192', objectives: [
        'Rapporter des faits d’actualité',
        'Commenter l’importance d’un événement',
        'Prendre position pour agir',
        'Informer sur des actions solidaires',
      ]},
    ],
  },
];

// ----------------------------------------------------------------------------
// Recursos descargables por libro (PDF/audio en S3, bucket PRIVADO
// galia-cours-internes-767968023146/mae/). Subidos con scripts/upload-cours-internes.mjs.
// Guardamos solo la KEY del objeto; la app pide una URL prefirmada de corta
// duración a /api/cours/sign (requiere token Galia). Sin acceso público.
// Subconjunto curado: Classe+ por dossier, guías, tests complémentaires (PDF) y
// los audios de esos tests. NO incluye las pistas del libro (CA/LE) ni los videos.
// ----------------------------------------------------------------------------

const pdfRes = (label: string, rel: string, teacherOnly = false): MaeResource => ({ label, key: 'mae/' + rel, kind: 'pdf', teacherOnly });
const audioRes = (label: string, rel: string, teacherOnly = false): MaeResource => ({ label, key: 'mae/' + rel, kind: 'audio', teacherOnly });

/** Classe+ de MAE 2: 8 dossiers × (Fenêtre sur + 3 leçons). Material de estudiante. */
function mae2ClassePlus(): MaeResource[] {
  const subs: Array<[string, string]> = [
    ['fenetre-sur', 'Fenêtre sur'], ['lecon-1', 'Leçon 1'], ['lecon-2', 'Leçon 2'], ['lecon-3', 'Leçon 3'],
  ];
  const items: MaeResource[] = [];
  for (let n = 1; n <= 8; n++) {
    for (const [key, txt] of subs) items.push(pdfRes(`Dossier ${n} · ${txt}`, `mae2/classe-plus/d${n}-${key}.pdf`));
  }
  return items;
}

/** Audios de los tests complémentaires (book 2 o 3). Material docente. */
function testAudios(book: 2 | 3, count: number): MaeResource[] {
  const items: MaeResource[] = [];
  for (let i = 1; i <= count; i++) {
    const p = String(i).padStart(2, '0');
    items.push(audioRes(`Audio test — piste ${i}`, `mae${book}/tests/audio/piste-${p}.mp3`, true));
  }
  return items;
}

export const MAE_RESOURCES: Record<number, MaeResourceGroup[]> = {
  1: [
    { title: 'Guide pédagogique', items: [pdfRes('Guide pédagogique (complet)', 'mae1/guide-pedagogique.pdf', true)] },
    { title: 'Classe+', items: [pdfRes('Livret Classe+', 'mae1/classe-plus-livret.pdf')] },
    { title: 'Documents de référence', items: [
      pdfRes('Précis grammatical', 'mae1/precis-grammatical.pdf'),
      pdfRes('Tableau de conjugaisons', 'mae1/tableau-conjugaisons.pdf'),
      pdfRes('Les sons du français', 'mae1/les-sons-du-francais.pdf'),
      pdfRes('Transcriptions, corrigés & lexique (livre)', 'mae1/transcriptions-corriges-lexique-livre.pdf'),
      pdfRes('Transcriptions, corrigés & lexique (cahier)', 'mae1/transcriptions-corriges-lexique-cahier.pdf'),
    ] },
  ],
  2: [
    { title: 'Guide pédagogique', items: [pdfRes('Guide pédagogique', 'mae2/guide-pedagogique.pdf', true)] },
    { title: 'Classe+ (par dossier)', items: mae2ClassePlus() },
    { title: 'Tests complémentaires', items: [
      pdfRes('Tests (dossiers 1–8)', 'mae2/tests/tests.pdf', true),
      pdfRes('Tests — corrigés', 'mae2/tests/tests-corriges.pdf', true),
      ...testAudios(2, 9),
    ] },
  ],
  3: [
    { title: 'Livre & corrigés', items: [
      pdfRes('Cahier d’activités', 'mae3/cahier-activites.pdf'),
      pdfRes('Corrigé du cahier', 'mae3/corrige-cahier.pdf', true),
    ] },
    { title: 'Classe+ (par dossier)', items: Array.from({ length: 9 }, (_, i) => pdfRes(`Dossier ${i + 1}`, `mae3/classe-plus/d${i + 1}.pdf`)) },
    { title: 'Tests complémentaires', items: [
      pdfRes('Tests (dossiers 1–9)', 'mae3/tests/tests.pdf', true),
      pdfRes('Tests — corrigés', 'mae3/tests/tests-corriges.pdf', true),
      ...testAudios(3, 11),
    ] },
  ],
};

/**
 * Catálogo de los Cours internes (método Mon alter ego) para el modo PROFESOR.
 * Le da a Galia el mapa libro→nivel→objetivos para alinear actividades con los
 * cursos presenciales de la AF.
 */
export function buildCoursInternesCatalogueForTeachers(): string {
  const blocks = MAE_BOOKS.map((b) => {
    const lines: string[] = [];
    lines.push(`• ${b.title} — ${b.levelRange}.`);
    if (b.note) lines.push(`  ⚠️ ${b.note}`);
    for (const m of b.modules) {
      lines.push(`  ${m.module} (${m.dossiers}, p. ${m.pages}) : ${m.objectives.join(' ; ')}.`);
    }
    return lines.join('\n');
  });
  return `CATÁLOGO DE LOS COURS INTERNES (cours présentiels SUPER / INTENSIF / SEMI de la AF, méthode « Mon alter ego ») :
${blocks.join('\n')}

Asociación rápida libro ↔ nivel : Mon alter ego 1 = A1–A2 · Mon alter ego 2 = A2–B1 · Mon alter ego 3 = B1–B2.
Cuando el grupo del profesor sigue un curso presencial, alinea las actividades con el libro y el módulo correspondiente (objetivos arriba), y combínalas si quieres con la temporada Nomade del mismo nivel.`;
}
